// Svelte 5-compatible URL localization utilities
// Replacement for sveltekit-localize-url to fix Svelte 5 incompatibility issues

import { error } from '@sveltejs/kit';

const settings = {
    baseLocale: '',
    locales: [],
    paramRegistry: new Map(),
    isInitialized: false
};

/**
 * Initializes the library.
 * @param baseLocale Default locale.
 * @param locales Enabled locales.
 * @param addBaseLocaleToPath If true, the correct pathname for `/page` is `/baseLocale/page`.
 * @param localeHosts Locales matched by hosts.
 */
export function initLocalizeUrl(newSettings) {
    if (settings.isInitialized) return;

    if (newSettings.localeHosts) {
        if (!(newSettings.baseLocale in newSettings.localeHosts)) {
            throw new Error(`Host for the base locale "${newSettings.baseLocale}" is not specified`);
        }
        newSettings.addBaseLocaleToPath = false;
    }

    Object.assign(settings, newSettings);
    settings.isInitialized = true;
}

// Helper to check if string is a locale
const isLocale = (locale) => settings.locales.includes(locale);

/**
 * Detects URL locale based on the pathname or the host.
 */
export function getLocaleFromUrl(url) {
    const [, lang] = url.pathname.split('/');
    if (isLocale(lang)) return lang;

    if (settings.localeHosts) {
        for (const [locale, host] of Object.entries(settings.localeHosts)) {
            if (host === url.host) {
                return locale;
            }
        }
    }

    return settings.baseLocale;
}

/**
 * Registers localized param.
 * @param position Param segment position in the URL starting with 1.
 * @param name Unique param name that matches router param name.
 * @param values Supported locales and their corresponding values.
 */
export function localizeParam(position, name, values) {
    const result = {
        name,
        position,
        values
    };
    settings.paramRegistry.set(name, result);
    return result;
}

/**
 * Checks if the route param value matches any of the localized param values.
 */
export function matchParam(routeParamValue, param) {
    return Object.values(param.values).includes(routeParamValue);
}

/**
 * Builds new localized page object.
 * @param url Current URL.
 * @param routeId Matched route id.
 */
export function localizePage(url, routeId) {
    const paramPositions = new Map();

    if (routeId) {
        const parts = routeId.split('/');
        for (let pos = 2; pos < parts.length; pos++) {
            const part = parts[pos];
            if (part[0] !== '[') continue;

            let name = part[1] === '[' ? part.slice(2, -2) : part.slice(1, -1);
            name = name.split('=')[0];
            paramPositions.set(name, pos - 1);
        }
    }

    return {
        hreflang: new Map(),
        switcher: new Map(),
        _url: new URL(url),
        _routeId: routeId,
        _paramPositions: paramPositions,
        _customParams: new Map(),
        _isDirty: true,
        _isNotAvailableForLocale: false
    };
}

/**
 * Set locales and matching values for dynamic params.
 */
export function setPageParam(page, name, values) {
    const position = page._paramPositions.get(name);
    if (!position) {
        console.error(`i18n-url: Param "${name}" isn't used at the route "${page._routeId}"`);
        return;
    }

    page._customParams.set(name, {
        position,
        name,
        values
    });
    page._isDirty = true;
}

/**
 * Retrieves page params (localized and optionally custom).
 * Includes defensive error handling for Svelte 5 compatibility.
 */
export function getPageParams(page, includeCustom = false) {
    // Defensive check for undefined or malformed page object
    if (!page || !page._paramPositions) {
        console.warn('i18n-url: Invalid page object passed to getPageParams');
        return [];
    }

    const params = [];

    try {
        for (const [name] of page._paramPositions) {
            let param = settings.paramRegistry.get(name);
            if (!param && includeCustom) {
                param = page._customParams.get(name);
            }
            if (param) {
                params.push(param);
            }
        }
    } catch (e) {
        console.warn('i18n-url: Error in getPageParams:', e);
        return [];
    }

    return params;
}

/**
 * Generates localized href for a page.
 */
export function href(page, locale, pathData, fallbackLocale = false) {
    // Defensive checks for Svelte 5 compatibility
    if (!page) {
        console.warn('i18n-url: Invalid page object passed to href');
        return '';
    }

    let parts;
    let usedParams;

    if (typeof pathData === 'string') {
        // new pathname
        usedParams = [];
        parts = pathData.split('/');

        // find params that are used in the pathname
        for (let i = 1; i < parts.length; i++) {
            const part = parts[i];
            if (part[0] !== '[') continue;

            const name = part.slice(1, -1);
            let param = settings.paramRegistry.get(name);

            if (!param && page._customParams) {
                param = page._customParams.get(name);
            }

            if (!param) {
                console.error(`i18n-url: Param "${name}" from path "${pathData}" doesn't exist at route "${page._routeId}"`);
                return '';
            }

            if (param.position !== i) {
                console.error(`i18n-url: Param "${name}" from path "${pathData}" is in wrong position`);
                return '';
            }

            usedParams.push(param);
        }
    } else {
        // build on top of a base pathname
        parts = pathData.base.split('/');
        usedParams = pathData.params || [];
    }

    if (parts[1] === '' || isLocale(parts[1])) {
        // remove locale for now
        parts.splice(1, 1);
    }

    // replace parts of the original pathname
    for (const name of usedParams) {
        let param = typeof name === 'string' ? settings.paramRegistry.get(name) : name;

        if (!param && page._customParams) {
            param = page._customParams.get(name);
        }

        if (!param) {
            console.error(`i18n-url: Param "${name}" doesn't exist at route "${page._routeId}"`);
            return '';
        }

        const value = param.values[locale];
        if (value) {
            parts[param.position] = value;
            continue;
        }

        // no path for this locale - use fallback if specified
        if (fallbackLocale) {
            if (fallbackLocale === true) {
                return href(page, settings.baseLocale, pathData);
            }
            if (typeof fallbackLocale === 'string') {
                return href(page, fallbackLocale, pathData);
            }
            for (const fallback of fallbackLocale) {
                if (fallback === locale) continue;
                const fallbackHref = href(page, fallback, pathData);
                if (fallbackHref) {
                    return fallbackHref;
                }
            }
        }

        return '';
    }

    // assemble final href
    let pathname = parts.join('/');

    // copy url without search and hash
    const url = new URL('/', page._url);

    if (settings.localeHosts) {
        let host = settings.localeHosts[locale];
        if (!host) {
            // use base locale host but add locale to pathname
            host = settings.localeHosts[settings.baseLocale];
            pathname = `/${locale}${pathname}`;
        }
        url.host = host;
        url.pathname = pathname;
        return url.href;
    }

    // add locale to pathname if needed
    if (settings.addBaseLocaleToPath || settings.baseLocale !== locale) {
        pathname = `/${locale}${pathname}`;
    }

    url.pathname = pathname;
    return url.href;
}

/**
 * Validates that the page is available for the current locale.
 * Includes defensive error handling for Svelte 5 compatibility.
 */
export function validatePage(page, config) {
    // Defensive check for undefined or malformed page object
    if (!page) {
        console.warn('i18n-url: Invalid page object passed to validatePage');
        return;
    }

    try {
        // Check if page is available for current locale
        if (page._isNotAvailableForLocale && config?.notFound) {
            throw error(404, config.notFound);
        }
    } catch (e) {
        // Only re-throw if it's a SvelteKit error
        if (e && e.status === 404) {
            throw e;
        }
        // Log other errors but don't crash
        console.warn('i18n-url: Error in validatePage:', e);
    }
}

/**
 * Generates hreflang links for a page.
 */
export function buildHreflang(page) {
    // Defensive check
    if (!page || !page.hreflang) {
        console.warn('i18n-url: Invalid page object passed to buildHreflang');
        return [];
    }

    const links = [];

    try {
        for (const [locale, href] of page.hreflang) {
            links.push({ locale, href });
        }
    } catch (e) {
        console.warn('i18n-url: Error in buildHreflang:', e);
        return [];
    }

    return links;
}

/**
 * Gets locale home href.
 */
export function getLocaleHomeHref(locale, currentUrl, leaveSearch = false) {
    const url = new URL(currentUrl);
    url.pathname = '/';

    if (!leaveSearch) {
        url.search = '';
        url.hash = '';
    }

    if (settings.localeHosts) {
        const host = settings.localeHosts[locale];
        if (host) {
            url.host = host;
            return url.href;
        }
    }

    // add locale prefix
    if (settings.addBaseLocaleToPath || settings.baseLocale !== locale) {
        url.pathname = `/${locale}`;
    }

    return url.href;
}